// EmployeeVehicleFormModal.tsx
import React, { useState, useEffect, useMemo } from 'react';
import { Modal, Form, Button, Spinner } from 'react-bootstrap';
import Select from 'react-select';
import apiDefinitions from "../../../../core/api/apiDefinitions";
import { showError } from "../../../../core/alert/sweetAlertConfig";
import { FormData, FormErrors, VehicleType, VehicleBrand, Employee } from './types';

interface Props {
  show: boolean;
  onHide: () => void;
  formData: FormData;
  errors: FormErrors;
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => void;
  onSubmit: (e: React.FormEvent) => Promise<void>;
  submitting: boolean;
  isEditMode: boolean;
  vehicleTypes: VehicleType[];
  vehicleBrands: VehicleBrand[];
}

const EmployeeVehicleFormModal: React.FC<Props> = ({
  show,
  onHide,
  formData,
  errors,
  onChange,
  onSubmit,
  submitting,
  isEditMode,
  vehicleTypes,
  vehicleBrands,
}) => {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);

  useEffect(() => {
    if (show) {
      fetchAllEmployees();
    }
  }, [show]);

  const fetchAllEmployees = async () => {
    setLoadingEmployees(true);
    const allEmployees: Employee[] = [];
    let page = 1;
    let hasMore = true;

    try {
      while (hasMore) {
        const payload = {
          filterType: "",
          filterValue: "",
          date: "",
        };

        const response = await apiDefinitions.getUsers(page, payload);

        if (response.data?.status === "S1000" && response.data.data?.users) {
          const usersPage = response.data.data.users.map((u: any) => ({
            empNumber: String(u.empNumber),
            name: u.name || "Unknown",
            email: u.email,
            status: u.status,
          }));

          allEmployees.push(...usersPage);

          const total = response.data.data.total || 0;
          hasMore = allEmployees.length < total && usersPage.length === 10;
          page++;
        } else {
          hasMore = false;
        }
      }
      setEmployees(allEmployees);
    } catch (err) {
      showError("Failed to load employees");
    } finally {
      setLoadingEmployees(false);
    }
  };

  const employeeOptions = useMemo(() => 
    employees.map(emp => ({
      value: emp.empNumber,
      label: `${emp.name} (${emp.empNumber}) - ${emp.status}${emp.email ? ` - ${emp.email}` : ''}`,
    })),
    [employees]
  );

  const selectedEmployee = employeeOptions.find(opt => opt.value === formData.employeeNumber) || null;

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          {isEditMode ? "Edit Vehicle Assign" : "Assign Vehicle to Employee"}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Form onSubmit={onSubmit}>
          <div className="row g-3">
            {/* Employee - Searchable */}
            <div className="col-md-6">
              <Form.Label>Employee <span className="text-danger">*</span></Form.Label>

              {loadingEmployees ? (
                <div className="d-flex align-items-center">
                  <Spinner animation="border" size="sm" className="me-2" />
                  <span>Loading employees...</span>
                </div>
              ) : (
                <>
                  <Select
                    value={selectedEmployee}
                    onChange={(selected) => {
                      const fakeEvent = {
                        target: {
                          name: "employeeNumber",
                          value: selected?.value || "",
                        }
                      } as React.ChangeEvent<HTMLInputElement>;
                      onChange(fakeEvent);
                    }}
                    options={employeeOptions}
                    placeholder="Search by name or employee number..."
                    isSearchable
                    isClearable
                    noOptionsMessage={() => "No employees found"}
                    styles={{
                      control: (base) => ({
                        ...base,
                        borderColor: errors.employeeNumber ? '#dc3545' : base.borderColor,
                      }),
                      menu: (base) => ({ ...base, zIndex: 9999 }),
                    }}
                  />
                  {errors.employeeNumber && (
                    <div className="text-danger mt-1" style={{ fontSize: '0.875rem' }}>
                      {errors.employeeNumber}
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Vehicle Type */}
            <div className="col-md-6">
              <Form.Label>Vehicle Type <span className="text-danger">*</span></Form.Label>
              <Form.Select
                name="vehicleTypeId"
                value={formData.vehicleTypeId}
                onChange={onChange}
                isInvalid={!!errors.vehicleTypeId}
              >
                <option value="">Select Type</option>
                {vehicleTypes.map(t => (
                  <option key={t.id} value={t.id}>{t.type}</option>
                ))}
              </Form.Select>
              <Form.Control.Feedback type="invalid">{errors.vehicleTypeId}</Form.Control.Feedback>
            </div>

            {/* Vehicle Brand */}
            <div className="col-md-6">
              <Form.Label>Vehicle Brand <span className="text-danger">*</span></Form.Label>
              <Form.Select
                name="vehicleBrandId"
                value={formData.vehicleBrandId}
                onChange={onChange}
                isInvalid={!!errors.vehicleBrandId}
              >
                <option value="">Select Brand</option>
                {vehicleBrands.map(b => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </Form.Select>
              <Form.Control.Feedback type="invalid">{errors.vehicleBrandId}</Form.Control.Feedback>
            </div>

            {/* Vehicle Color */}
            <div className="col-md-6">
              <Form.Label>Vehicle Color <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="text"
                name="vehicleColor"
                value={formData.vehicleColor}
                onChange={onChange}
                isInvalid={!!errors.vehicleColor}
                placeholder="e.g. White, Black, Silver"
              />
              <Form.Control.Feedback type="invalid">{errors.vehicleColor}</Form.Control.Feedback>
            </div>

            {/* Number Plate */}
            <div className="col-6">
              <Form.Label>Number Plate <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="text"
                name="vehicleNumberPlate"
                value={formData.vehicleNumberPlate}
                onChange={onChange}
                isInvalid={!!errors.vehicleNumberPlate}
                placeholder="e.g. ABC-1234"
                maxLength={10}
              />
              <Form.Control.Feedback type="invalid">{errors.vehicleNumberPlate}</Form.Control.Feedback>
            </div>

            <div className="col-md-6">
              <Form.Label>Daily Limit <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="number"
                name="limitPerDay"
                value={formData.limitPerDay}
                onChange={onChange}
                isInvalid={!!errors.limitPerDay}
                placeholder="e.g. 200"
                min="0"
              />
              <Form.Control.Feedback type="invalid">
                {errors.limitPerDay || "Please enter daily limit (0 or more)"}
              </Form.Control.Feedback>
            </div>

            {/* Status - ONLY in Edit mode */}
            {isEditMode && (
              <div className="col-md-6">
                <Form.Label>Assign Status <span className="text-danger">*</span></Form.Label>
                <Form.Select
                  name="status"
                  value={formData.status || ""}
                  onChange={onChange}
                  isInvalid={!!errors.status}
                >
                  <option value="">Select Status</option>
                  <option value="ACTIVE">ACTIVE</option>
                  <option value="INACTIVE">INACTIVE</option>
                </Form.Select>
                {errors.status && (
                  <div className="text-danger mt-1" style={{ fontSize: '0.875rem' }}>
                    {errors.status}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="d-flex justify-content-end gap-3 mt-4">
            <Button variant="secondary" onClick={onHide}>
              Cancel
            </Button>
            <Button 
              variant="success" 
              type="submit" 
              disabled={submitting || loadingEmployees}
            >
              {submitting ? (
                <>
                  <Spinner as="span" animation="border" size="sm" className="me-2" />
                  {isEditMode ? "Updating..." : "Assigning..."}
                </>
              ) : isEditMode ? "Update Assign" : "Assign Vehicle"}
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default EmployeeVehicleFormModal;